import {
  create,
  type Dependencies,
  type Model,
  type Input as ModelInput,
  type Output as ModelOutput,
} from "./illumination.model"

export const OUTPUT_TYPE = {
  PROGRESS: "liqa_illumination_progress",
  READY: "liqa_illumination_ready",
  RESULT: "liqa_illumination_result",
  DESTROYED: "liqa_illumination_destroyed",
} as const

export type ProgressOutput = { type: typeof OUTPUT_TYPE.PROGRESS; progress: number }
export type ReadyOutput = { type: typeof OUTPUT_TYPE.READY }
export type ResultOutput = {
  type: typeof OUTPUT_TYPE.RESULT
  timestamp: number
  result: ModelOutput
}
export type DestroyedOutput = { type: typeof OUTPUT_TYPE.DESTROYED }
export type Output = ProgressOutput | ReadyOutput | ResultOutput | DestroyedOutput

export const INPUT_TYPE = {
  INIT: "liqa_illumination_init",
  DATA: "liqa_illumination_data",
  DESTROY: "liqa_illumination_destroy",
} as const

export type InitInput = {
  type: typeof INPUT_TYPE.INIT
  dependencies: Dependencies
}
export type DataInput = { type: typeof INPUT_TYPE.DATA; timestamp: number } & ModelInput
export type DestroyInput = { type: typeof INPUT_TYPE.DESTROY }
export type Input = InitInput | DataInput | DestroyInput

let model: Model | null = null

const handleInit = async (e: InitInput) => {
  model = await create(e.dependencies, {
    onProgress: (progress) =>
      self.postMessage({ type: OUTPUT_TYPE.PROGRESS, progress } satisfies ProgressOutput),
  })

  self.postMessage({ type: OUTPUT_TYPE.READY } satisfies ReadyOutput)
}
const handlePredict = async (e: DataInput) => {
  if (!model) throw new Error("Illumination Worker: Model is not initialized")

  self.postMessage(
    {
      type: OUTPUT_TYPE.RESULT,
      timestamp: e.timestamp,
      result: await model.predict(e),
    } satisfies ResultOutput,
    { transfer: [e.frame.data.buffer] },
  )
}
const handleDestroy = async () => {
  if (!model) return

  await model.destroy()

  self.postMessage({ type: OUTPUT_TYPE.DESTROYED } satisfies DestroyedOutput)
}

self.onmessage = async (e: MessageEvent<Input>) => {
  if (e.data.type === INPUT_TYPE.INIT) handleInit(e.data)
  else if (e.data.type === INPUT_TYPE.DATA) handlePredict(e.data)
  else if (e.data.type === INPUT_TYPE.DESTROY) handleDestroy()
}
